import React, { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import { getSupabaseClient } from '../../lib/supabase';
import { APIProvider, Map, AdvancedMarker, Pin, useMap } from '@vis.gl/react-google-maps';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { ConfirmModal } from './ConfirmModal';
import { 
  MapPin, 
  Search, 
  Store, 
  Users, 
  Loader2, 
  XCircle, 
  RefreshCw, 
  ChevronDown, 
  ChevronRight,
  Layers, 
  CheckCircle2, 
  AlertTriangle, 
  Trash2, 
  ShieldCheck, 
  ShoppingBag, 
  UtensilsCrossed, 
  Truck, 
  HeartPulse, 
  Wrench,
  Radio,
  Sliders,
  Plus,
  GraduationCap,
  Scissors,
  Tractor,
  Factory,
  Hotel,
  Bus,
  Stethoscope,
  Car,
  QrCode,
  Video,
  ScanLine,
  Bike,
  Siren,
  MonitorSmartphone,
  Pill,
  ChefHat,
  CreditCard,
  TestTube,
  Bed,
  CalendarCheck,
  Utensils,
  LayoutDashboard,
  Sprout,
  User,
  Settings,
  Building2,
  Recycle,
  Briefcase,
  Pencil
} from 'lucide-react';

const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || '';

interface PlatformServiceMaster {
  id: string;
  module_key: string;
  service_key: string;
  service_label: string;
  description: string;
  icon: string | null;
  display_order: number;
  is_active: boolean;
  service_group?: string;
}

const SERVICE_ICON_MAP: Record<string, React.ComponentType<{ size?: number; className?: string }>> = {
  GraduationCap,
  Scissors,
  Store,
  Tractor,
  Factory,
  Hotel,
  Bus,
  Stethoscope,
  Car,
  QrCode,
  Video,
  ScanLine,
  Wrench,
  Bike,
  Siren,
  MonitorSmartphone,
  Truck,
  Pill,
  ChefHat,
  CreditCard,
  TestTube,
  ShoppingBag,
  Bed,
  CalendarCheck,
  Utensils,
  LayoutDashboard,
  Sprout,
  User,
  Settings,
  Building2,
  Recycle,
  Briefcase,
};

const ALLOWED_MERCHANT_SERVICES = [
  'Retail POS',
  'Hotel/Restaurant',
  'Health Care',
  'Manufacturing ERP',
  'Education',
  'Transport Management',
  'Agri Management',
  'Daily Services',
];

const ALLOWED_PUBLIC_SERVICES = [
  'Mart',
  'Scan & Go',
  'Stay',
  'Book Table',
  'Food',
  'Care',
  'Move',
  'Classified',
  'Agri-Tech',
  'MyLife',
  'Utility',
  'Restaurant',
  'Bus/Taxi',
  'Property',
  'Buy & Sell',
  'Jobs',
  'Home Services',
];

function renderServiceIcon(iconName: string | null, className = "text-primary shrink-0") {
  if (!iconName) return <Layers size={16} className={className} />;
  const Comp = SERVICE_ICON_MAP[iconName] || Layers;
  return <Comp size={16} className={className} />;
}

interface ServiceableArea {
  id: string;
  area_name: string;
  area_type: string;
  center_lat: number;
  center_lng: number;
  radius_meters: number;
  polygon_geojson?: any;
  pincode_count: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface AreaServiceStatus {
  id?: string;
  serviceable_area_id: string;
  platform_service_id: string;
  is_active: boolean;
}

interface CoveredPincode {
  id: string;
  pincode: string;
  distance_meters?: number;
  created_at: string;
}

// Preset Indian Hubs for quick navigation
const QUICK_HUBS = [
  { name: 'Mumbai Central', lat: 19.0760, lng: 72.8777 },
  { name: 'Delhi NCR', lat: 28.6139, lng: 77.2090 },
  { name: 'Bengaluru', lat: 12.9716, lng: 77.5946 },
  { name: 'Hyderabad', lat: 17.3850, lng: 78.4867 },
  { name: 'Pune', lat: 18.5204, lng: 73.8567 },
  { name: 'Jaipur', lat: 26.9124, lng: 75.7873 },
  { name: 'Ahmedabad', lat: 23.0225, lng: 72.5714 },
  { name: 'Kolkata', lat: 22.5726, lng: 88.3639 },
  { name: 'Chennai', lat: 13.0827, lng: 80.2707 },
];

const MODULE_CONFIG: Record<string, { label: string; icon: any; color: string; badge: string }> = {
  retail: { label: 'Retail & Quick Commerce', icon: ShoppingBag, color: 'text-blue-500', badge: 'bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300' },
  hospitality: { label: 'Hospitality & Dining', icon: UtensilsCrossed, color: 'text-orange-500', badge: 'bg-orange-100 text-orange-700 dark:bg-orange-950 dark:text-orange-300' },
  logistics: { label: 'Logistics & Mobility', icon: Truck, color: 'text-purple-500', badge: 'bg-purple-100 text-purple-700 dark:bg-purple-950 dark:text-purple-300' },
  healthcare: { label: 'Healthcare & SOS', icon: HeartPulse, color: 'text-rose-500', badge: 'bg-rose-100 text-rose-700 dark:bg-rose-950 dark:text-rose-300' },
  services: { label: 'Home & Professional Services', icon: Wrench, color: 'text-emerald-500', badge: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300' },
};

/**
 * Custom circle overlay component that renders directly on Google Maps
 */
function MapCircleOverlay({
  center,
  radiusMeters,
  strokeColor = '#2563eb',
  fillColor = '#3b82f6',
  fillOpacity = 0.2,
}: {
  center: { lat: number; lng: number };
  radiusMeters: number;
  strokeColor?: string;
  fillColor?: string;
  fillOpacity?: number;
}) {
  const map = useMap();
  const circleRef = useRef<any>(null);

  useEffect(() => {
    if (!map || !(window as any).google?.maps?.Circle) return;

    if (!circleRef.current) {
      circleRef.current = new (window as any).google.maps.Circle({
        strokeColor,
        strokeOpacity: 0.8,
        strokeWeight: 2,
        fillColor,
        fillOpacity,
        map,
        center,
        radius: radiusMeters,
        clickable: false,
      });
    } else {
      circleRef.current.setCenter(center);
      circleRef.current.setRadius(radiusMeters);
      circleRef.current.setOptions({ strokeColor, fillColor, fillOpacity, clickable: false });
    }

    return () => {
      if (circleRef.current) {
        circleRef.current.setMap(null);
        circleRef.current = null;
      }
    };
  }, [map, center.lat, center.lng, radiusMeters, strokeColor, fillColor, fillOpacity]);

  return null;
}

/**
 * Controller to smoothly pan map when center changes via buttons/inputs,
 * without forcing controlled mode that freezes user panning/zooming.
 */
function MapCameraController({ center }: { center: { lat: number; lng: number } }) {
  const map = useMap();
  const prevCenterRef = useRef(center);

  useEffect(() => {
    if (!map) return;
    if (
      Math.abs(prevCenterRef.current.lat - center.lat) > 0.00001 ||
      Math.abs(prevCenterRef.current.lng - center.lng) > 0.00001
    ) {
      map.panTo(center);
      prevCenterRef.current = center;
    }
  }, [map, center.lat, center.lng]);

  return null;
}

// Searchable combo for legacy state/district/pincode selection
function SearchableCombo({
  options,
  value,
  onSelect,
  disabled,
  loading,
  placeholder = 'Search...',
}: {
  options: { id: string; label: string }[];
  value: string;
  onSelect: (id: string) => void;
  disabled?: boolean;
  loading?: boolean;
  placeholder?: string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setQuery('');
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selected = options.find(o => o.id === value);
  const filtered = query.trim()
    ? options.filter(o => o.label.toLowerCase().includes(query.trim().toLowerCase()))
    : options;

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen(o => !o)}
        className="flex h-10 w-full items-center justify-between rounded-md border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 px-3 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-50 dark:text-white"
      >
        <span className={selected ? 'dark:text-white' : 'text-slate-400'}>
          {loading ? 'Loading...' : (selected ? selected.label : placeholder)}
        </span>
        <ChevronDown size={16} className="opacity-50 shrink-0 ml-2" />
      </button>
      {open && !disabled && (
        <div className="absolute z-20 mt-1 w-full bg-white dark:bg-slate-800 border dark:border-slate-700 rounded-lg shadow-lg">
          <div className="p-2 border-b dark:border-slate-700">
            <Input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Type to search..."
              className="h-8 text-sm dark:bg-slate-900 dark:border-slate-700 dark:text-white"
            />
          </div>
          <div className="max-h-56 overflow-y-auto">
            {filtered.length === 0 && (
              <div className="px-3 py-2 text-sm text-slate-400">Koi match nahi mila</div>
            )}
            {filtered.map(o => (
              <button
                key={o.id}
                type="button"
                onClick={() => { onSelect(o.id); setOpen(false); setQuery(''); }}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-100 dark:hover:bg-slate-700 dark:text-white ${o.id === value ? 'bg-slate-50 dark:bg-slate-700/50 font-medium' : ''}`}
              >
                {o.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function ServiceableAreaControlView() {
  const supabase = getSupabaseClient();

  // Mode tab: 'map_zones' (Patch #21 primary) vs 'pincode_overrides' (Individual legacy pincode controls)
  const [controlTab, setControlTab] = useState<'map_zones' | 'pincode_overrides'>('map_zones');

  // Master Services Catalog
  const [platformServices, setPlatformServices] = useState<PlatformServiceMaster[]>([]);
  const [loadingServices, setLoadingServices] = useState(false);

  // Serviceable Areas from DB
  const [serviceableAreas, setServiceableAreas] = useState<ServiceableArea[]>([]);
  const [loadingAreas, setLoadingAreas] = useState(false);

  // Area Service Mappings: area_id -> Record<platform_service_id, boolean>
  const [areaServicesMap, setAreaServicesMap] = useState<Record<string, Record<string, boolean>>>({});
  const [togglingService, setTogglingService] = useState<string | null>(null);

  // New / Edit Area Form State
  const [editingArea, setEditingArea] = useState<ServiceableArea | null>(null);
  const [updatingArea, setUpdatingArea] = useState(false);
  const formCardRef = useRef<HTMLDivElement>(null);
  const [areaName, setAreaName] = useState('');
  const [centerLat, setCenterLat] = useState<number>(19.0760);
  const [centerLng, setCenterLng] = useState<number>(72.8777);
  const [radiusKm, setRadiusKm] = useState<number>(5);
  const [initialSelectedServices, setInitialSelectedServices] = useState<Record<string, boolean>>({});

  // Real-time Radius Pincode Preview
  const [previewPincodes, setPreviewPincodes] = useState<any[]>([]);
  const [loadingPreview, setLoadingPreview] = useState(false);

  // Creating & Syncing Actions
  const [creatingArea, setCreatingArea] = useState(false);
  const [syncingAreaId, setSyncingAreaId] = useState<string | null>(null);

  // Expanded Area for Viewing Covered Pincodes
  const [expandedAreaId, setExpandedAreaId] = useState<string | null>(null);
  const [coveredPincodes, setCoveredPincodes] = useState<CoveredPincode[]>([]);
  const [loadingCoveredPincodes, setLoadingCoveredPincodes] = useState(false);

  // Delete Area Safety Modal
  const [deleteAreaTarget, setDeleteAreaTarget] = useState<ServiceableArea | null>(null);
  const [deletingArea, setDeletingArea] = useState(false);

  // ==========================================
  // Pincode Override Tab State (Legacy hierarchy)
  // ==========================================
  const [states, setStates] = useState<{ id: string; name: string }[]>([]);
  const [districts, setDistricts] = useState<{ id: string; name: string }[]>([]);
  const [pincodes, setPincodes] = useState<any[]>([]);
  const [selectedStateId, setSelectedStateId] = useState('');
  const [selectedDistrictId, setSelectedDistrictId] = useState('');
  const [selectedPincodeId, setSelectedPincodeId] = useState('');
  const [loadingDistricts, setLoadingDistricts] = useState(false);
  const [loadingPincodes, setLoadingPincodes] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [searching, setSearching] = useState(false);
  const [selectedArea, setSelectedArea] = useState<any | null>(null);
  const [pincodeActionLoading, setPincodeActionLoading] = useState<string | null>(null);
  const [activeAreas, setActiveAreas] = useState<any[]>([]);
  const [loadingActiveAreas, setLoadingActiveAreas] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<any | null>(null);
  const [removingPincode, setRemovingPincode] = useState(false);

  // ==========================================
  // 1. Load Platform Services Master
  // ==========================================
  const loadPlatformServices = useCallback(async () => {
    if (!supabase) return;
    setLoadingServices(true);
    try {
      const { data, error } = await supabase
        .from('platform_services_master')
        .select('*')
        .order('display_order', { ascending: true });
      if (error) throw error;
      setPlatformServices(data || []);

      // Default select all active services for a new zone
      const initialMap: Record<string, boolean> = {};
      (data || []).forEach((s: PlatformServiceMaster) => {
        if (s.is_active) initialMap[s.id] = true;
      });
      setInitialSelectedServices(initialMap);
    } catch (err: any) {
      console.error('Platform services error:', err);
    } finally {
      setLoadingServices(false);
    }
  }, [supabase]);

  // ==========================================
  // 2. Load Serviceable Areas & Service Toggles
  // ==========================================
  const loadServiceableAreas = useCallback(async () => {
    if (!supabase) return;
    setLoadingAreas(true);
    try {
      const { data: areasData, error: areasErr } = await supabase
        .from('serviceable_areas')
        .select('*')
        .order('created_at', { ascending: false });
      if (areasErr) throw areasErr;

      setServiceableAreas(areasData || []);

      // Load all serviceable_area_services
      const { data: servicesData, error: servicesErr } = await supabase
        .from('serviceable_area_services')
        .select('*');
      if (!servicesErr && servicesData) {
        const mapping: Record<string, Record<string, boolean>> = {};
        servicesData.forEach((row: AreaServiceStatus) => {
          if (!mapping[row.serviceable_area_id]) {
            mapping[row.serviceable_area_id] = {};
          }
          mapping[row.serviceable_area_id][row.platform_service_id] = row.is_active;
        });
        setAreaServicesMap(mapping);
      }
    } catch (err: any) {
      toast.error('Serviceable areas load nahi ho sake: ' + (err.message || 'Error'));
    } finally {
      setLoadingAreas(false);
    }
  }, [supabase]);

  // ==========================================
  // 3. Real-time Pincodes in Radius Preview
  // ==========================================
  const updateRadiusPreview = useCallback(async (lat: number, lng: number, radKm: number) => {
    if (!supabase) return;
    setLoadingPreview(true);
    try {
      const { data, error } = await supabase.rpc('get_pincodes_in_radius', {
        p_center_lat: lat,
        p_center_lng: lng,
        p_radius_km: radKm,
      });
      if (error) throw error;
      setPreviewPincodes(data || []);
    } catch (err) {
      console.warn('Radius pincode preview error:', err);
      setPreviewPincodes([]);
    } finally {
      setLoadingPreview(false);
    }
  }, [supabase]);

  useEffect(() => {
    loadPlatformServices();
    loadServiceableAreas();
  }, [loadPlatformServices, loadServiceableAreas]);

  // Trigger preview when center or radius changes (debounced)
  useEffect(() => {
    const timer = setTimeout(() => {
      updateRadiusPreview(centerLat, centerLng, radiusKm);
    }, 400);
    return () => clearTimeout(timer);
  }, [centerLat, centerLng, radiusKm, updateRadiusPreview]);

  // ==========================================
  // 4. Create New Serviceable Area
  // ==========================================
  const handleCreateArea = async () => {
    if (!supabase) return;
    if (!areaName.trim()) {
      toast.error('Kripya Serviceable Area ka Naam daalein (e.g. Mumbai Central Hub)');
      return;
    }

    setCreatingArea(true);
    try {
      // 1. Insert row into serviceable_areas
      const { data: newArea, error: createErr } = await supabase
        .from('serviceable_areas')
        .insert({
          area_name: areaName.trim(),
          area_type: 'circle',
          center_lat: centerLat,
          center_lng: centerLng,
          radius_meters: Math.round(radiusKm * 1000),
          is_active: true,
        })
        .select()
        .single();

      if (createErr) throw createErr;
      if (!newArea) throw new Error('Area create nahi ho paya.');

      const areaId = newArea.id;

      // 2. Call sync_serviceable_area_pincodes to automatically resolve covered pincodes
      const { data: syncedCount, error: syncErr } = await supabase.rpc('sync_serviceable_area_pincodes', {
        p_area_id: areaId,
      });

      if (syncErr) {
        console.warn('Pincode sync warning:', syncErr);
      }

      // 3. Setup initial services via toggle_serviceable_area_service
      const servicePromises = Object.entries(initialSelectedServices)
        .filter(([_, isEnabled]) => isEnabled)
        .map(([serviceId]) =>
          supabase.rpc('toggle_serviceable_area_service', {
            p_area_id: areaId,
            p_platform_service_id: serviceId,
            p_is_active: true,
          })
        );

      await Promise.allSettled(servicePromises);

      toast.success(
        `Area "${areaName}" safalta-poorvak create ho gaya! (${syncedCount ?? previewPincodes.length} Pincodes covered)`
      );

      // Reset form
      setAreaName('');
      await loadServiceableAreas();
    } catch (err: any) {
      toast.error('Area create karne mein error: ' + (err.message || 'Kuch galat hua'));
    } finally {
      setCreatingArea(false);
    }
  };

  // ==========================================
  // 4b. Edit / Update Existing Serviceable Area
  // ==========================================
  const handleStartEditArea = (area: ServiceableArea) => {
    setEditingArea(area);
    setAreaName(area.area_name || '');
    setCenterLat(Number(area.center_lat) || 19.0760);
    setCenterLng(Number(area.center_lng) || 72.8777);
    setRadiusKm(Number(((area.radius_meters || 5000) / 1000).toFixed(1)));

    // Scroll smoothly to map form card
    formCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    toast.info(`Editing area "${area.area_name}". Map par boundary adjust karein.`);
  };

  const handleCancelEdit = () => {
    setEditingArea(null);
    setAreaName('');
    setCenterLat(19.0760);
    setCenterLng(72.8777);
    setRadiusKm(5);
  };

  const handleUpdateArea = async () => {
    if (!supabase || !editingArea) return;
    if (!areaName.trim()) {
      toast.error('Please enter the area name.');
      return;
    }

    setUpdatingArea(true);
    try {
      const updatePayload: any = {
        area_name: areaName.trim(),
        center_lat: centerLat,
        center_lng: centerLng,
        radius_meters: Math.round(radiusKm * 1000),
        updated_at: new Date().toISOString(),
      };

      if (editingArea.polygon_geojson) {
        updatePayload.polygon_geojson = editingArea.polygon_geojson;
      }

      // Update serviceable_areas directly in Supabase
      const { error: updateErr } = await supabase
        .from('serviceable_areas')
        .update(updatePayload)
        .eq('id', editingArea.id);

      if (updateErr) throw updateErr;

      // Recompute pincodes coverage via RPC
      const { data: syncedCount, error: syncErr } = await supabase.rpc('sync_serviceable_area_pincodes', {
        p_area_id: editingArea.id,
      });

      if (syncErr) {
        console.warn('Pincode sync warning on update:', syncErr);
      }

      const coveredCount = typeof syncedCount === 'number' ? syncedCount : (previewPincodes.length || 0);
      toast.success(`Area updated, ${coveredCount} pincodes now covered`);

      // Reset edit mode
      setEditingArea(null);
      setAreaName('');
      setCenterLat(19.0760);
      setCenterLng(72.8777);
      setRadiusKm(5);

      await loadServiceableAreas();
    } catch (err: any) {
      toast.error('Area update karne mein error: ' + (err.message || 'Kuch galat hua'));
    } finally {
      setUpdatingArea(false);
    }
  };

  // ==========================================
  // 5. Toggle Individual Platform Service
  // ==========================================
  const handleToggleService = async (areaId: string, serviceId: string, currentlyActive: boolean) => {
    if (!supabase) return;
    const targetState = !currentlyActive;
    const loadingKey = `${areaId}-${serviceId}`;
    setTogglingService(loadingKey);

    try {
      const { error } = await supabase.rpc('toggle_serviceable_area_service', {
        p_area_id: areaId,
        p_platform_service_id: serviceId,
        p_is_active: targetState,
      });

      if (error) throw error;

      // Optimistic update
      setAreaServicesMap(prev => ({
        ...prev,
        [areaId]: {
          ...(prev[areaId] || {}),
          [serviceId]: targetState,
        },
      }));

      const serviceObj = platformServices.find(s => s.id === serviceId);
      toast.success(
        `${serviceObj?.service_label || 'Service'} ko ${targetState ? 'ACTIVATE' : 'DEACTIVATE'} kar diya gaya.`
      );
    } catch (err: any) {
      toast.error('Service status badal nahi saka: ' + (err.message || 'Error'));
    } finally {
      setTogglingService(null);
    }
  };

  // ==========================================
  // 6. Sync Covered Pincodes on Demand
  // ==========================================
  const handleSyncPincodes = async (areaId: string, areaName: string) => {
    if (!supabase) return;
    setSyncingAreaId(areaId);
    try {
      const { data, error } = await supabase.rpc('sync_serviceable_area_pincodes', {
        p_area_id: areaId,
      });
      if (error) throw error;

      toast.success(`"${areaName}" ke pincodes re-sync ho gaye: ${data} pincodes linked.`);
      await loadServiceableAreas();

      if (expandedAreaId === areaId) {
        await loadCoveredPincodes(areaId);
      }
    } catch (err: any) {
      toast.error('Pincode sync fail ho gaya: ' + (err.message || 'Error'));
    } finally {
      setSyncingAreaId(null);
    }
  };

  // ==========================================
  // 7. Load Covered Pincodes for an Area
  // ==========================================
  const loadCoveredPincodes = async (areaId: string) => {
    if (!supabase) return;
    setLoadingCoveredPincodes(true);
    try {
      const { data, error } = await supabase
        .from('serviceable_area_pincodes')
        .select('*')
        .eq('serviceable_area_id', areaId)
        .order('distance_meters', { ascending: true });
      if (error) throw error;
      setCoveredPincodes(data || []);
    } catch (err: any) {
      toast.error('Pincodes load nahi hue: ' + (err.message || 'Error'));
    } finally {
      setLoadingCoveredPincodes(false);
    }
  };

  const handleToggleExpandArea = (areaId: string) => {
    if (expandedAreaId === areaId) {
      setExpandedAreaId(null);
      setCoveredPincodes([]);
    } else {
      setExpandedAreaId(areaId);
      loadCoveredPincodes(areaId);
    }
  };

  // ==========================================
  // 8. Delete Serviceable Area (With Delete Safety)
  // ==========================================
  const confirmDeleteArea = async () => {
    if (!supabase || !deleteAreaTarget) return;
    setDeletingArea(true);
    try {
      const { error } = await supabase.rpc('delete_serviceable_area', {
        p_area_id: deleteAreaTarget.id,
      });

      if (error) {
        // Handle trigger/delete safety check exception
        throw error;
      }

      toast.success(`Area "${deleteAreaTarget.area_name}" safalta-poorvak delete ho gaya.`);
      setDeleteAreaTarget(null);
      await loadServiceableAreas();
    } catch (err: any) {
      // Show user-friendly safety trigger message
      toast.error('Area delete nahi ho saka: ' + (err.message || 'Safety block trigger'));
    } finally {
      setDeletingArea(false);
    }
  };

  // ==========================================
  // 9. Legacy Pincode Override Helpers
  // ==========================================
  useEffect(() => {
    if (!supabase || controlTab !== 'pincode_overrides') return;
    (async () => {
      const { data, error } = await supabase.from('states').select('id, name').order('name');
      if (error) { toast.error('States load nahi ho paaye: ' + error.message); return; }
      setStates(data || []);
    })();
  }, [supabase, controlTab]);

  const loadActivePincodesList = useCallback(async () => {
    if (!supabase) return;
    setLoadingActiveAreas(true);
    const { data, error } = await supabase
      .from('pincodes')
      .select('id, pincode, is_serviceable, merchant_signup_enabled, customer_signup_enabled, districts(name, states(name))')
      .eq('is_serviceable', true)
      .order('pincode');
    setLoadingActiveAreas(false);
    if (error) { toast.error('Active list load nahi ho paayi: ' + error.message); return; }
    const rows = (data || []).map((row: any) => ({
      pincode_id: row.id,
      pincode: row.pincode,
      is_serviceable: row.is_serviceable,
      merchant_signup_enabled: row.merchant_signup_enabled,
      customer_signup_enabled: row.customer_signup_enabled,
      district_name: row.districts?.name || '-',
      state_name: row.districts?.states?.name || '-',
    }));
    setActiveAreas(rows);
  }, [supabase]);

  useEffect(() => {
    if (controlTab === 'pincode_overrides') {
      loadActivePincodesList();
    }
  }, [controlTab, loadActivePincodesList]);

  const handleStateChange = async (stateId: string) => {
    setSelectedStateId(stateId);
    setSelectedDistrictId('');
    setSelectedPincodeId('');
    setDistricts([]);
    setPincodes([]);
    setSelectedArea(null);
    if (!supabase || !stateId) return;
    setLoadingDistricts(true);
    const { data, error } = await supabase
      .from('districts')
      .select('id, name')
      .eq('state_id', stateId)
      .order('name')
      .limit(2000);
    setLoadingDistricts(false);
    if (error) { toast.error('Districts load nahi ho paaye: ' + error.message); return; }
    setDistricts(data || []);
  };

  const handleDistrictChange = async (districtId: string) => {
    setSelectedDistrictId(districtId);
    setSelectedPincodeId('');
    setPincodes([]);
    setSelectedArea(null);
    if (!supabase || !districtId) return;
    setLoadingPincodes(true);
    const { data, error } = await supabase
      .from('pincodes')
      .select('id, pincode, is_serviceable, merchant_signup_enabled, customer_signup_enabled')
      .eq('district_id', districtId)
      .order('pincode')
      .limit(2000);
    setLoadingPincodes(false);
    if (error) { toast.error('Pincodes load nahi ho paaye: ' + error.message); return; }
    setPincodes(data || []);
  };

  const handlePincodeChange = (pincodeId: string) => {
    setSelectedPincodeId(pincodeId);
    const p = pincodes.find(x => x.id === pincodeId);
    if (!p) { setSelectedArea(null); return; }
    const stateName = states.find(s => s.id === selectedStateId)?.name || '-';
    const districtName = districts.find(d => d.id === selectedDistrictId)?.name || '-';
    setSelectedArea({
      pincode_id: p.id,
      pincode: p.pincode,
      district_name: districtName,
      state_name: stateName,
      is_serviceable: p.is_serviceable,
      merchant_signup_enabled: p.merchant_signup_enabled,
      customer_signup_enabled: p.customer_signup_enabled,
    });
  };

  useEffect(() => {
    if (!supabase || controlTab !== 'pincode_overrides') return;
    if (searchTerm.trim().length < 2) { setSearchResults([]); return; }
    const t = setTimeout(async () => {
      setSearching(true);
      const { data, error } = await supabase.rpc('admin_search_pincodes', {
        p_search: searchTerm.trim(),
        p_state_id: null,
        p_district_id: null,
        p_limit: 20,
      });
      setSearching(false);
      if (error) { toast.error('Search fail: ' + error.message); return; }
      setSearchResults(data || []);
    }, 350);
    return () => clearTimeout(t);
  }, [searchTerm, supabase, controlTab]);

  const handleToggleMerchant = async (area: any, enable: boolean) => {
    if (!supabase) return;
    setPincodeActionLoading(area.pincode_id + '-merchant');
    try {
      if (enable && !area.is_serviceable) {
        const { error: svcErr } = await supabase.rpc('admin_set_pincode_serviceable', {
          p_pincode_id: area.pincode_id, p_is_serviceable: true,
        });
        if (svcErr) throw svcErr;
      }
      const { error } = await supabase.rpc('admin_set_merchant_signup', {
        p_pincode_id: area.pincode_id, p_enabled: enable,
      });
      if (error) throw error;
      toast.success(`Merchant Signup ${area.pincode} ke liye ${enable ? 'ACTIVATE' : 'DEACTIVATE'} ho gaya`);
      if (selectedArea?.pincode_id === area.pincode_id) {
        setSelectedArea({ ...selectedArea, is_serviceable: true, merchant_signup_enabled: enable });
      }
      await loadActivePincodesList();
    } catch (err: any) {
      toast.error(err.message || 'Kuch galat ho gaya');
    } finally {
      setPincodeActionLoading(null);
    }
  };

  const handleToggleCustomer = async (area: any, enable: boolean) => {
    if (!supabase) return;
    setPincodeActionLoading(area.pincode_id + '-customer');
    try {
      if (enable && !area.is_serviceable) {
        const { error: svcErr } = await supabase.rpc('admin_set_pincode_serviceable', {
          p_pincode_id: area.pincode_id, p_is_serviceable: true,
        });
        if (svcErr) throw svcErr;
      }
      const { error } = await supabase.rpc('admin_set_customer_signup', {
        p_pincode_id: area.pincode_id, p_enabled: enable,
      });
      if (error) throw error;
      toast.success(`Public App Signup ${area.pincode} ke liye ${enable ? 'ACTIVATE' : 'DEACTIVATE'} ho gaya`);
      if (selectedArea?.pincode_id === area.pincode_id) {
        setSelectedArea({ ...selectedArea, is_serviceable: true, customer_signup_enabled: enable });
      }
      await loadActivePincodesList();
    } catch (err: any) {
      toast.error(err.message || 'Kuch galat ho gaya');
    } finally {
      setPincodeActionLoading(null);
    }
  };

  const confirmRemovePincode = async () => {
    if (!supabase || !removeTarget) return;
    setRemovingPincode(true);
    try {
      const { error } = await supabase.rpc('admin_set_pincode_serviceable', {
        p_pincode_id: removeTarget.pincode_id, p_is_serviceable: false,
      });
      if (error) throw error;
      toast.success(`${removeTarget.pincode} list se remove ho gaya`);
      if (selectedArea?.pincode_id === removeTarget.pincode_id) {
        setSelectedArea({ ...selectedArea, is_serviceable: false, merchant_signup_enabled: false, customer_signup_enabled: false });
      }
      await loadActivePincodesList();
      setRemoveTarget(null);
    } catch (err: any) {
      toast.error(err.message || 'Kuch galat ho gaya');
    } finally {
      setRemovingPincode(false);
    }
  };

  // Group services by module_key
  const servicesByModule = platformServices.reduce((acc, svc) => {
    if (!acc[svc.module_key]) acc[svc.module_key] = [];
    acc[svc.module_key].push(svc);
    return acc;
  }, {} as Record<string, PlatformServiceMaster[]>);

  // Group services by service_group for Default Services selection
  // 1. Merchant Services: service_group = 'merchant_module' (8 services)
  const merchantServices = platformServices
    .filter(s => s.service_group === 'merchant_module' && ALLOWED_MERCHANT_SERVICES.includes(s.service_label))
    .sort((a, b) => {
      if ((a.display_order ?? 0) !== (b.display_order ?? 0)) {
        return (a.display_order ?? 0) - (b.display_order ?? 0);
      }
      return ALLOWED_MERCHANT_SERVICES.indexOf(a.service_label) - ALLOWED_MERCHANT_SERVICES.indexOf(b.service_label);
    });

  // 2. Public Services: service_group = 'public_service' (exactly 17 services in exact order)
  const publicServicesRecord: Record<string, PlatformServiceMaster> = {};
  platformServices
    .filter(s => s.service_group === 'public_service' && ALLOWED_PUBLIC_SERVICES.includes(s.service_label))
    .forEach(s => {
      if (!publicServicesRecord[s.service_label] || s.is_active) {
        publicServicesRecord[s.service_label] = s;
      }
    });

  const publicServices: PlatformServiceMaster[] = Object.values(publicServicesRecord)
    .sort((a, b) => {
      if ((a.display_order ?? 0) !== (b.display_order ?? 0)) {
        return (a.display_order ?? 0) - (b.display_order ?? 0);
      }
      return ALLOWED_PUBLIC_SERVICES.indexOf(a.service_label) - ALLOWED_PUBLIC_SERVICES.indexOf(b.service_label);
    });

  return (
    <div className="space-y-6">
      {/* Header & Mode Switcher */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <MapPin size={22} className="text-primary" /> Serviceable Area Control
          </h2>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            Google Maps par interactive geographic zones (radius circle) define karein aur har area mein Consumer-facing revolutionary services ko toggle karein.
          </p>
        </div>

        {/* Tab Switcher */}
        <div className="inline-flex rounded-lg border border-slate-200 dark:border-slate-800 bg-slate-100 dark:bg-slate-900 p-1 self-start sm:self-auto">
          <button
            onClick={() => setControlTab('map_zones')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${
              controlTab === 'map_zones'
                ? 'bg-white dark:bg-slate-800 text-slate-900 dark:text-white shadow-sm'
                : 'text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white'
            }`}
          >
            <Radio size={14} className={controlTab === 'map_zones' ? 'text-primary' : ''} />
            Map Zones & Services
          </button>
          <button
            onClick={() => setControlTab('pincode_overrides')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${
              controlTab === 'pincode_overrides'
                ? 'bg-white dark:bg-slate-800 text-slate-900 dark:text-white shadow-sm'
                : 'text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white'
            }`}
          >
            <Sliders size={14} className={controlTab === 'pincode_overrides' ? 'text-primary' : ''} />
            Pincode Signup Overrides
          </button>
        </div>
      </div>

      {/* ======================================================== */}
      {/* TAB 1: MAP ZONES & SERVICE GATING (Patch #21 PRIMARY)     */}
      {/* ======================================================== */}
      {controlTab === 'map_zones' && (
        <div className="space-y-6">
          {/* Create / Edit Zone Card */}
          <div ref={formCardRef}>
            <Card className={`dark:bg-slate-900 dark:border-slate-800 border-none shadow-sm transition-all ${
              editingArea ? 'ring-2 ring-amber-500/50 dark:ring-amber-500/30' : ''
            }`}>
              <CardHeader>
                <CardTitle className="text-base dark:text-white flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    {editingArea ? (
                      <>
                        <Pencil size={18} className="text-amber-500" />
                        <span>Serviceable Area Edit Karein: <span className="text-amber-600 dark:text-amber-400 font-bold">{editingArea.area_name}</span></span>
                      </>
                    ) : (
                      <>
                        <Plus size={18} className="text-primary" />
                        <span>Naya Serviceable Area / Zone Banayein</span>
                      </>
                    )}
                  </span>
                  <span className="text-xs font-normal text-slate-500">
                    {editingArea ? 'Editing Mode' : 'Interactive Circle Radius Selection'}
                  </span>
                </CardTitle>
                <CardDescription className="dark:text-slate-400">
                  {editingArea
                    ? 'Area ka naam, center coordinates ya radius circle update karein. Save karne par boundary ke anusaar pincodes re-sync ho jayenge.'
                    : 'Map par click karke center chunein, radius set karein, aur area save karein. Pincodes automatically resolve hokar database mein link ho jayenge.'
                  }
                </CardDescription>
              </CardHeader>
            <CardContent className="space-y-5">
              {/* Form Inputs & Quick Hubs */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="text-xs font-medium text-slate-700 dark:text-slate-300 mb-1 block">
                    Area / Zone Name *
                  </label>
                  <Input
                    placeholder="e.g. Mumbai South Hub, Indiranagar Zone"
                    value={areaName}
                    onChange={(e) => setAreaName(e.target.value)}
                    className="dark:bg-slate-950 dark:border-slate-800"
                  />
                </div>

                <div>
                  <label className="text-xs font-medium text-slate-700 dark:text-slate-300 mb-1 block">
                    Radius (Kilometers): <span className="font-bold text-primary">{radiusKm} km</span> ({radiusKm * 1000}m)
                  </label>
                  <div className="flex items-center gap-2 mt-1">
                    <input
                      type="range"
                      min={1}
                      max={50}
                      step={0.5}
                      value={radiusKm}
                      onChange={(e) => setRadiusKm(parseFloat(e.target.value))}
                      className="w-full accent-primary h-2 bg-slate-200 dark:bg-slate-800 rounded-lg cursor-pointer"
                    />
                    <div className="flex gap-1">
                      {[3, 5, 10, 20].map((r) => (
                        <button
                          key={r}
                          type="button"
                          onClick={() => setRadiusKm(r)}
                          className={`px-2 py-0.5 text-[11px] rounded font-medium border ${
                            radiusKm === r
                              ? 'bg-primary text-white border-primary'
                              : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-700'
                          }`}
                        >
                          {r}k
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                <div>
                  <label className="text-xs font-medium text-slate-700 dark:text-slate-300 mb-1 block">
                    Quick Center Jump (City Hub)
                  </label>
                  <Select
                    onValueChange={(val) => {
                      const found = QUICK_HUBS.find(h => h.name === val);
                      if (found) {
                        setCenterLat(found.lat);
                        setCenterLng(found.lng);
                      }
                    }}
                  >
                    <SelectTrigger className="w-full dark:bg-slate-950 dark:border-slate-800 text-xs">
                      <SelectValue placeholder="Chunein City Hub..." />
                    </SelectTrigger>
                    <SelectContent>
                      {QUICK_HUBS.map(hub => (
                        <SelectItem key={hub.name} value={hub.name}>
                          {hub.name} ({hub.lat.toFixed(2)}, {hub.lng.toFixed(2)})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Center Coordinate manual display/edit */}
              <div className="flex flex-wrap items-center gap-4 text-xs text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-950 p-2.5 rounded-lg border border-slate-200 dark:border-slate-800">
                <span className="font-semibold text-slate-700 dark:text-slate-300 flex items-center gap-1">
                  <MapPin size={14} className="text-rose-500" /> Center Coordinates:
                </span>
                <div className="flex items-center gap-1">
                  <span>Lat:</span>
                  <input
                    type="number"
                    step="0.0001"
                    value={centerLat}
                    onChange={(e) => setCenterLat(parseFloat(e.target.value) || 0)}
                    className="w-24 px-1.5 py-0.5 rounded border dark:bg-slate-900 dark:border-slate-700 font-mono text-xs"
                  />
                </div>
                <div className="flex items-center gap-1">
                  <span>Lng:</span>
                  <input
                    type="number"
                    step="0.0001"
                    value={centerLng}
                    onChange={(e) => setCenterLng(parseFloat(e.target.value) || 0)}
                    className="w-24 px-1.5 py-0.5 rounded border dark:bg-slate-900 dark:border-slate-700 font-mono text-xs"
                  />
                </div>
                <span className="text-slate-400">| Map par click karke bhi center move kiya ja sakta hai</span>
              </div>

              {/* Interactive Google Map Container */}
              <div className="relative rounded-xl overflow-hidden border border-slate-200 dark:border-slate-800 shadow-inner h-[380px] w-full">
                {GOOGLE_MAPS_API_KEY ? (
                  <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
                    <Map
                      defaultCenter={{ lat: centerLat, lng: centerLng }}
                      defaultZoom={12}
                      gestureHandling="greedy"
                      disableDefaultUI={false}
                      onClick={(e) => {
                        if (e.detail?.latLng) {
                          setCenterLat(Number(e.detail.latLng.lat.toFixed(6)));
                          setCenterLng(Number(e.detail.latLng.lng.toFixed(6)));
                        }
                      }}
                      mapId="SERVICEABLE_AREAS_ADMIN_MAP"
                      className="w-full h-full"
                    >
                      <MapCameraController center={{ lat: centerLat, lng: centerLng }} />

                      {/* Current Draft / Edit Circle & Draggable Center Pin */}
                      <AdvancedMarker
                        position={{ lat: centerLat, lng: centerLng }}
                        draggable={true}
                        onDragEnd={(e) => {
                          if (e.latLng) {
                            const latVal = typeof e.latLng.lat === 'function' ? e.latLng.lat() : (e.latLng as any).lat;
                            const lngVal = typeof e.latLng.lng === 'function' ? e.latLng.lng() : (e.latLng as any).lng;
                            setCenterLat(Number(latVal.toFixed(6)));
                            setCenterLng(Number(lngVal.toFixed(6)));
                          }
                        }}
                        title={editingArea ? `${areaName} (Editing - Drag marker to change center)` : (areaName ? `${areaName} (Drag to change center)` : 'Zone Center (Drag marker to move)')}
                      >
                        <Pin background={editingArea ? "#f59e0b" : "#2563eb"} glyphColor="#ffffff" borderColor="#ffffff" />
                      </AdvancedMarker>
                      <MapCircleOverlay
                        center={{ lat: centerLat, lng: centerLng }}
                        radiusMeters={radiusKm * 1000}
                        strokeColor={editingArea ? "#d97706" : "#2563eb"}
                        fillColor={editingArea ? "#f59e0b" : "#3b82f6"}
                        fillOpacity={0.25}
                      />

                      {/* Render other existing saved areas as light green or grey circles */}
                      {serviceableAreas
                        .filter((sa) => !editingArea || sa.id !== editingArea.id)
                        .map((sa) => (
                        <React.Fragment key={sa.id}>
                          <AdvancedMarker
                            position={{ lat: Number(sa.center_lat), lng: Number(sa.center_lng) }}
                            title={sa.area_name}
                          >
                            <Pin
                              background={sa.is_active ? '#10b981' : '#94a3b8'}
                              glyphColor="#ffffff"
                              borderColor="#ffffff"
                              scale={0.8}
                            />
                          </AdvancedMarker>
                          <MapCircleOverlay
                            center={{ lat: Number(sa.center_lat), lng: Number(sa.center_lng) }}
                            radiusMeters={sa.radius_meters || 5000}
                            strokeColor={sa.is_active ? '#10b981' : '#94a3b8'}
                            fillColor={sa.is_active ? '#10b981' : '#94a3b8'}
                            fillOpacity={0.12}
                          />
                        </React.Fragment>
                      ))}
                    </Map>
                  </APIProvider>
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center bg-slate-100 dark:bg-slate-950 p-6 text-center">
                    <MapPin size={40} className="text-slate-400 mb-2 animate-bounce" />
                    <p className="font-semibold text-slate-800 dark:text-slate-200">Google Maps View</p>
                    <p className="text-xs text-slate-500 max-w-sm mt-1">
                      Coordinates: Lat {centerLat}, Lng {centerLng} • Radius {radiusKm} km ({radiusKm * 1000}m)
                    </p>
                  </div>
                )}

                {/* Map Floating Summary Pill */}
                <div className="absolute top-3 left-3 bg-white/95 dark:bg-slate-900/95 backdrop-blur-md px-3 py-1.5 rounded-lg shadow-md border border-slate-200 dark:border-slate-800 text-xs flex items-center gap-2 pointer-events-none select-none">
                  <span className="w-2.5 h-2.5 rounded-full bg-blue-500 animate-pulse" />
                  <span className="font-medium text-slate-800 dark:text-slate-200">
                    Radius: {radiusKm} km
                  </span>
                  <span className="text-slate-400">|</span>
                  <span className="text-emerald-600 dark:text-emerald-400 font-semibold flex items-center gap-1">
                    {loadingPreview ? (
                      <Loader2 size={12} className="animate-spin" />
                    ) : (
                      `${previewPincodes.length} Pincodes covered`
                    )}
                  </span>
                </div>
              </div>

              {/* Covered Pincodes Preview Ribbon */}
              <div className="bg-slate-50 dark:bg-slate-950 p-3 rounded-lg border border-slate-200 dark:border-slate-800 space-y-2">
                <div className="flex items-center justify-between text-xs">
                  <span className="font-medium text-slate-700 dark:text-slate-300 flex items-center gap-1.5">
                    <CheckCircle2 size={14} className="text-emerald-500" />
                    Iss Circle Radius mein aane wale Pincodes ({previewPincodes.length}):
                  </span>
                  <span className="text-slate-500 text-[11px]">
                    RPC: get_pincodes_in_radius
                  </span>
                </div>

                {previewPincodes.length === 0 ? (
                  <p className="text-xs text-slate-400 italic">
                    {loadingPreview ? 'Pincodes search kiye ja rahe hain...' : 'Iss radius mein koi recorded pincode nahi mila. Kripya radius badhayein ya center change karein.'}
                  </p>
                ) : (
                  <div className="flex flex-wrap gap-1.5 max-h-24 overflow-y-auto pt-1">
                    {previewPincodes.slice(0, 35).map((p, idx) => (
                      <span
                        key={p.pincode || idx}
                        className="px-2 py-0.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded text-xs font-mono text-slate-700 dark:text-slate-200"
                        title={p.distance_km ? `${p.distance_km.toFixed(2)} km door` : ''}
                      >
                        {p.pincode}
                        {p.distance_km !== undefined && (
                          <span className="text-[10px] text-slate-400 ml-1">({p.distance_km.toFixed(1)}k)</span>
                        )}
                      </span>
                    ))}
                    {previewPincodes.length > 35 && (
                      <span className="text-xs text-slate-400 self-center">
                        +{previewPincodes.length - 35} aur pincodes...
                      </span>
                    )}
                  </div>
                )}
              </div>

              {/* Activate Default Services for New Area (Only for New Area creation) */}
              {!editingArea ? (
                <div className="space-y-4">
                  <h3 className="text-xs font-semibold text-slate-700 dark:text-slate-300">
                    Activate Default Services for New Area
                  </h3>

                  {/* Section 1: Merchant Services */}
                  <div className="space-y-2">
                    <div className="flex items-center gap-1.5 text-xs font-bold text-slate-700 dark:text-slate-200">
                      <Store size={14} className="text-blue-600" />
                      <span>Merchant Services</span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
                      {merchantServices.map((svc) => {
                        const isChecked = !!initialSelectedServices[svc.id];
                        return (
                          <label
                            key={svc.id}
                            className={`flex items-center gap-2.5 p-2.5 rounded-lg border text-xs cursor-pointer transition-colors ${
                              isChecked
                                ? 'bg-blue-50/60 dark:bg-blue-950/30 border-blue-200 dark:border-blue-800'
                                : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 opacity-60'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={(e) => {
                                setInitialSelectedServices(prev => ({
                                  ...prev,
                                  [svc.id]: e.target.checked,
                                }));
                              }}
                              className="rounded accent-primary text-primary shrink-0"
                            />
                            <div className="flex items-center gap-2 min-w-0 flex-1">
                              {renderServiceIcon(svc.icon, "text-blue-600 dark:text-blue-400 shrink-0")}
                              <p className="font-medium text-slate-800 dark:text-slate-200 truncate">
                                {svc.service_label}
                              </p>
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  </div>

                  {/* Section 2: Public Services */}
                  <div className="space-y-2 pt-1">
                    <div className="flex items-center gap-1.5 text-xs font-bold text-slate-700 dark:text-slate-200">
                      <Users size={14} className="text-emerald-600" />
                      <span>Public Services</span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
                      {publicServices.map((svc) => {
                        const isChecked = !!initialSelectedServices[svc.id];
                        return (
                          <label
                            key={svc.id}
                            className={`flex items-center gap-2.5 p-2.5 rounded-lg border text-xs cursor-pointer transition-colors ${
                              isChecked
                                ? 'bg-emerald-50/50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-800'
                                : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 opacity-60'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={(e) => {
                                setInitialSelectedServices(prev => ({
                                  ...prev,
                                  [svc.id]: e.target.checked,
                                }));
                              }}
                              className="rounded accent-emerald-600 text-emerald-600 shrink-0"
                            />
                            <div className="flex items-center gap-2 min-w-0 flex-1">
                              {renderServiceIcon(svc.icon, "text-emerald-600 dark:text-emerald-400 shrink-0")}
                              <p className="font-medium text-slate-800 dark:text-slate-200 truncate">
                                {svc.service_label}
                              </p>
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="p-3.5 rounded-lg bg-amber-50/70 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-900/40 text-xs text-amber-800 dark:text-amber-300 flex items-center gap-2.5">
                  <ShieldCheck size={18} className="text-amber-600 dark:text-amber-400 shrink-0" />
                  <div>
                    <span className="font-semibold">Boundary Edit Mode:</span> Iss area ki active services (Merchant Services aur Public Services) bilkul surakshit rahengi. Services ko toggle karne ke liye neeche diye gaye <em>Service Gating Control</em> matrix ka upyog karein.
                  </div>
                </div>
              )}

              {/* Submit / Update Actions */}
              <div className="flex items-center justify-end gap-3 pt-2">
                {editingArea && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleCancelEdit}
                    disabled={updatingArea}
                    className="dark:border-slate-700"
                  >
                    Cancel
                  </Button>
                )}
                {editingArea ? (
                  <Button
                    onClick={handleUpdateArea}
                    disabled={updatingArea || !areaName.trim()}
                    className="bg-amber-600 hover:bg-amber-700 text-white flex items-center gap-2"
                  >
                    {updatingArea ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />}
                    Area Update Karein & Pincodes Re-Sync Karein
                  </Button>
                ) : (
                  <Button
                    onClick={handleCreateArea}
                    disabled={creatingArea || !areaName.trim()}
                    className="bg-primary hover:bg-primary/90 text-white flex items-center gap-2"
                  >
                    {creatingArea ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />}
                    Serviceable Area Banayein & Pincodes Link Karein
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
          </div>

          {/* ======================================================== */}
          {/* Active Serviceable Areas & Service Gating Matrix         */}
          {/* ======================================================== */}
          <Card className="dark:bg-slate-900 dark:border-slate-800 border-none shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between pb-3">
              <div>
                <CardTitle className="text-base dark:text-white flex items-center gap-2">
                  <Layers size={18} className="text-primary" /> Active Serviceable Areas ({serviceableAreas.length})
                </CardTitle>
                <CardDescription className="dark:text-slate-400">
                  Har area ke liye covered pincodes sync karein aur platform services (Scan-and-Go, Delivery, QR Dining, SOS, etc.) ko on/off karein.
                </CardDescription>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={loadServiceableAreas}
                disabled={loadingAreas}
                className="dark:border-slate-700"
              >
                <RefreshCw size={14} className={loadingAreas ? 'animate-spin' : ''} />
              </Button>
            </CardHeader>

            <CardContent className="space-y-4">
              {serviceableAreas.length === 0 ? (
                <div className="text-center py-12 border border-dashed border-slate-200 dark:border-slate-800 rounded-xl">
                  <MapPin size={36} className="mx-auto text-slate-300 dark:text-slate-600 mb-2" />
                  <p className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                    Abhi koi Serviceable Area nahi banaya gaya hai.
                  </p>
                  <p className="text-xs text-slate-400 mt-1 max-w-sm mx-auto">
                    Upar diye gaye Google Map aur Radius selector se apna pehla operational hub banayein.
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {serviceableAreas.map((area) => {
                    const isExpanded = expandedAreaId === area.id;
                    const areaServices = areaServicesMap[area.id] || {};
                    const isSyncing = syncingAreaId === area.id;

                    return (
                      <div
                        key={area.id}
                        className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden shadow-sm hover:border-slate-300 dark:hover:border-slate-700 transition-colors"
                      >
                        {/* Area Summary Header Bar */}
                        <div className="p-4 bg-slate-50/70 dark:bg-slate-800/40 border-b border-slate-200 dark:border-slate-800 flex flex-col md:flex-row md:items-center justify-between gap-3">
                          <div className="flex items-center gap-3">
                            <div className="p-2.5 rounded-lg bg-primary/10 text-primary">
                              <MapPin size={20} />
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <h3 className="font-bold text-slate-900 dark:text-white text-base">
                                  {area.area_name}
                                </h3>
                                <span
                                  className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                                    area.is_active
                                      ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300'
                                      : 'bg-slate-200 text-slate-700 dark:bg-slate-800 dark:text-slate-400'
                                  }`}
                                >
                                  {area.is_active ? 'ACTIVE' : 'INACTIVE'}
                                </span>
                              </div>
                              <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                                Type: <span className="font-semibold">{area.area_type}</span> • Radius:{' '}
                                <span className="font-semibold text-primary">{((area.radius_meters || 5000) / 1000).toFixed(1)} km</span> •
                                Center: {Number(area.center_lat).toFixed(4)}, {Number(area.center_lng).toFixed(4)}
                              </p>
                            </div>
                          </div>

                          {/* Quick Actions for Area */}
                          <div className="flex flex-wrap items-center gap-2">
                            {/* Expand/Collapse Pincodes Button */}
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleToggleExpandArea(area.id)}
                              className="text-xs flex items-center gap-1.5"
                            >
                              <span className="font-bold text-primary">{area.pincode_count || 0}</span> Pincodes
                              {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                            </Button>

                            {/* Re-Sync Pincodes */}
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleSyncPincodes(area.id, area.area_name)}
                              disabled={isSyncing}
                              className="text-xs flex items-center gap-1 dark:border-slate-700"
                              title="Re-compute and link pincodes within this circle"
                            >
                              <RefreshCw size={13} className={isSyncing ? 'animate-spin' : ''} />
                              Sync Pincodes
                            </Button>

                            {/* Edit Area */}
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleStartEditArea(area)}
                              className="text-xs flex items-center gap-1 dark:border-slate-700 text-amber-600 dark:text-amber-400 hover:text-amber-700 hover:bg-amber-50 dark:hover:bg-amber-950/30"
                              title="Edit this area boundary and details"
                            >
                              <Pencil size={13} />
                              Edit
                            </Button>

                            {/* Delete Area */}
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => setDeleteAreaTarget(area)}
                              className="text-xs flex items-center gap-1"
                            >
                              <Trash2 size={13} />
                              Delete
                            </Button>
                          </div>
                        </div>

                        {/* Collapsible Covered Pincodes Drawer */}
                        {isExpanded && (
                          <div className="p-4 bg-slate-100/50 dark:bg-slate-950/50 border-b border-slate-200 dark:border-slate-800 text-xs space-y-2">
                            <div className="flex items-center justify-between">
                              <span className="font-semibold text-slate-700 dark:text-slate-300">
                                Serviceable Area Pincodes ({coveredPincodes.length}):
                              </span>
                              <span className="text-slate-400 text-[11px]">Table: serviceable_area_pincodes</span>
                            </div>

                            {loadingCoveredPincodes ? (
                              <div className="flex items-center gap-2 text-slate-500 py-2">
                                <Loader2 size={14} className="animate-spin" /> Pincodes load ho rahe hain...
                              </div>
                            ) : coveredPincodes.length === 0 ? (
                              <p className="text-slate-400 italic">
                                Koi pincode link nahi hai. "Sync Pincodes" button daba kar resolve karein.
                              </p>
                            ) : (
                              <div className="flex flex-wrap gap-1.5 max-h-36 overflow-y-auto">
                                {coveredPincodes.map((cp) => (
                                  <span
                                    key={cp.id}
                                    className="px-2 py-0.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded text-xs font-mono"
                                  >
                                    {cp.pincode}
                                    {cp.distance_meters !== undefined && (
                                      <span className="text-[10px] text-slate-400 ml-1">
                                        ({(cp.distance_meters / 1000).toFixed(1)}km)
                                      </span>
                                    )}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        )}

                        {/* Service Gating Matrix for this Area */}
                        <div className="p-4 space-y-3">
                          <div className="flex items-center justify-between">
                            <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 flex items-center gap-1.5">
                              <ShieldCheck size={14} className="text-primary" /> Service Gating Control (Consumer App Services)
                            </h4>
                            <span className="text-[11px] text-slate-400">
                              Table: serviceable_area_services • RPC: toggle_serviceable_area_service
                            </span>
                          </div>

                          {/* Group by Module */}
                          <div className="space-y-3">
                            {Object.entries(servicesByModule).map(([moduleKey, services]) => {
                              const modConfig = MODULE_CONFIG[moduleKey] || {
                                label: moduleKey,
                                icon: Layers,
                                color: 'text-slate-500',
                                badge: 'bg-slate-100 text-slate-700',
                              };
                              const ModIcon = modConfig.icon;

                              return (
                                <div key={moduleKey} className="space-y-1.5">
                                  <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-700 dark:text-slate-300">
                                    <ModIcon size={14} className={modConfig.color} />
                                    <span>{modConfig.label}</span>
                                  </div>

                                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
                                    {services.map((svc) => {
                                      const isEnabled = areaServices[svc.id] !== false; // Default true if active
                                      const isToggling = togglingService === `${area.id}-${svc.id}`;

                                      return (
                                        <div
                                          key={svc.id}
                                          className={`flex items-center justify-between p-2.5 rounded-lg border text-xs transition-colors ${
                                            isEnabled
                                              ? 'bg-slate-50 dark:bg-slate-800/60 border-slate-200 dark:border-slate-700'
                                              : 'bg-red-50/30 dark:bg-red-950/10 border-red-200 dark:border-red-900/40 opacity-75'
                                          }`}
                                        >
                                          <div className="pr-2">
                                            <p className="font-semibold text-slate-900 dark:text-white leading-tight">
                                              {svc.service_label}
                                            </p>
                                            <span
                                              className={`inline-block text-[10px] font-bold mt-1 px-1.5 py-0.2 rounded ${
                                                isEnabled
                                                  ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-400'
                                                  : 'bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-400'
                                              }`}
                                            >
                                              {isEnabled ? 'ENABLED' : 'DISABLED'}
                                            </span>
                                          </div>

                                          <Button
                                            size="sm"
                                            variant={isEnabled ? 'destructive' : 'default'}
                                            disabled={isToggling}
                                            onClick={() => handleToggleService(area.id, svc.id, isEnabled)}
                                            className="h-7 px-2.5 text-[11px] shrink-0"
                                          >
                                            {isToggling ? (
                                              <Loader2 size={12} className="animate-spin" />
                                            ) : isEnabled ? (
                                              'Turn Off'
                                            ) : (
                                              'Turn On'
                                            )}
                                          </Button>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* ======================================================== */}
      {/* TAB 2: PINCODE OVERRIDES (Legacy hierarchy & search)      */}
      {/* ======================================================== */}
      {controlTab === 'pincode_overrides' && (
        <div className="space-y-6">
          <Card className="dark:bg-slate-900 dark:border-slate-800 border-none shadow-sm">
            <CardHeader>
              <CardTitle className="text-base dark:text-white">State / District / Pincode Signup Overrides</CardTitle>
              <CardDescription className="dark:text-slate-400">
                Country → State → District → Pincode select karke Merchant App aur Public/Customer App ka signup alag-alag Activate/Deactivate karein.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <div>
                  <label className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1 block">Country</label>
                  <div className="flex h-10 w-full items-center rounded-md border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 px-3 py-2 text-sm text-slate-500 dark:text-slate-400">
                    India
                  </div>
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1 block">State</label>
                  <SearchableCombo
                    options={states.map(s => ({ id: s.id, label: s.name }))}
                    value={selectedStateId}
                    onSelect={handleStateChange}
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1 block">District</label>
                  <SearchableCombo
                    options={districts.map(d => ({ id: d.id, label: d.name }))}
                    value={selectedDistrictId}
                    onSelect={handleDistrictChange}
                    disabled={!selectedStateId}
                    loading={loadingDistricts}
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1 block">Pincode</label>
                  <SearchableCombo
                    options={pincodes.map(p => ({ id: p.id, label: p.pincode }))}
                    value={selectedPincodeId}
                    onSelect={handlePincodeChange}
                    disabled={!selectedDistrictId}
                    loading={loadingPincodes}
                  />
                </div>
              </div>

              <div className="relative">
                <label className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1 block">
                  Ya kisi bhi field se seedha dhundhein (Pincode / District / State)
                </label>
                <div className="relative">
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="e.g. 400001 ya Mumbai ya Maharashtra"
                    className="pl-9 dark:bg-slate-800 dark:border-slate-700 dark:text-white"
                  />
                  {searching && <Loader2 size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 animate-spin" />}
                </div>
                {searchResults.length > 0 && (
                  <div className="absolute z-10 mt-1 w-full bg-white dark:bg-slate-800 border dark:border-slate-700 rounded-lg shadow-lg max-h-64 overflow-y-auto">
                    {searchResults.map(r => (
                      <button
                        key={r.pincode_id}
                        onClick={() => {
                          setSelectedArea(r);
                          setSearchTerm('');
                          setSearchResults([]);
                        }}
                        className="w-full text-left px-4 py-2.5 hover:bg-slate-100 dark:hover:bg-slate-700 text-sm border-b dark:border-slate-700 last:border-0"
                      >
                        <span className="font-medium text-slate-900 dark:text-white">{r.pincode}</span>
                        <span className="text-slate-500 dark:text-slate-400"> — {r.district_name}, {r.state_name}</span>
                        {r.is_serviceable && <span className="ml-2 text-xs text-emerald-600 dark:text-emerald-400">(Active)</span>}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Selected Pincode Control Card */}
          {selectedArea && (
            <Card className="dark:bg-slate-900 dark:border-slate-800 border-none shadow-sm">
              <CardHeader>
                <CardTitle className="text-base dark:text-white flex items-center gap-2">
                  <MapPin size={16} /> {selectedArea.pincode} — {selectedArea.district_name}, {selectedArea.state_name}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-800 rounded-lg border dark:border-slate-700">
                    <div className="flex items-center gap-3">
                      <Store size={20} className="text-primary" />
                      <div>
                        <p className="text-sm font-medium dark:text-white">Merchant App</p>
                        <span className={`text-xs font-medium ${selectedArea.merchant_signup_enabled ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-500 dark:text-red-400'}`}>
                          {selectedArea.merchant_signup_enabled ? 'ACTIVE' : 'INACTIVE'}
                        </span>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" disabled={selectedArea.merchant_signup_enabled || pincodeActionLoading === selectedArea.pincode_id + '-merchant'} onClick={() => handleToggleMerchant(selectedArea, true)}>
                        {pincodeActionLoading === selectedArea.pincode_id + '-merchant' ? <Loader2 size={14} className="animate-spin" /> : 'Activate'}
                      </Button>
                      <Button size="sm" variant="destructive" disabled={!selectedArea.merchant_signup_enabled || pincodeActionLoading === selectedArea.pincode_id + '-merchant'} onClick={() => handleToggleMerchant(selectedArea, false)}>
                        Deactivate
                      </Button>
                    </div>
                  </div>

                  <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-800 rounded-lg border dark:border-slate-700">
                    <div className="flex items-center gap-3">
                      <Users size={20} className="text-primary" />
                      <div>
                        <p className="text-sm font-medium dark:text-white">Public / Customer App</p>
                        <span className={`text-xs font-medium ${selectedArea.customer_signup_enabled ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-500 dark:text-red-400'}`}>
                          {selectedArea.customer_signup_enabled ? 'ACTIVE' : 'INACTIVE'}
                        </span>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" disabled={selectedArea.customer_signup_enabled || pincodeActionLoading === selectedArea.pincode_id + '-customer'} onClick={() => handleToggleCustomer(selectedArea, true)}>
                        {pincodeActionLoading === selectedArea.pincode_id + '-customer' ? <Loader2 size={14} className="animate-spin" /> : 'Activate'}
                      </Button>
                      <Button size="sm" variant="destructive" disabled={!selectedArea.customer_signup_enabled || pincodeActionLoading === selectedArea.pincode_id + '-customer'} onClick={() => handleToggleCustomer(selectedArea, false)}>
                        Deactivate
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Table of Active Pincodes */}
          <Card className="dark:bg-slate-900 dark:border-slate-800 border-none shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-base dark:text-white">Individual Active Pincodes ({activeAreas.length})</CardTitle>
              <Button size="sm" variant="outline" onClick={loadActivePincodesList} disabled={loadingActiveAreas}>
                <RefreshCw size={14} className={loadingActiveAreas ? 'animate-spin' : ''} />
              </Button>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-left">
                  <thead className="bg-slate-50 dark:bg-slate-800/50 text-slate-600 dark:text-slate-400 font-medium">
                    <tr>
                      <th className="px-6 py-3">State</th>
                      <th className="px-6 py-3">District</th>
                      <th className="px-6 py-3">Pincode</th>
                      <th className="px-6 py-3">Merchant App</th>
                      <th className="px-6 py-3">Public App</th>
                      <th className="px-6 py-3 text-right">Remove</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200 dark:divide-slate-800 dark:bg-slate-900">
                    {activeAreas.length === 0 && (
                      <tr><td colSpan={6} className="px-6 py-8 text-center text-slate-400">Abhi koi pincode active nahi hai.</td></tr>
                    )}
                    {activeAreas.map(area => (
                      <tr key={area.pincode_id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                        <td className="px-6 py-3 dark:text-slate-300">{area.state_name}</td>
                        <td className="px-6 py-3 dark:text-slate-300">{area.district_name}</td>
                        <td className="px-6 py-3 font-medium dark:text-white">{area.pincode}</td>
                        <td className="px-6 py-3">
                          <button
                            onClick={() => handleToggleMerchant(area, !area.merchant_signup_enabled)}
                            disabled={pincodeActionLoading === area.pincode_id + '-merchant'}
                            className={`px-2 py-1 rounded text-xs font-semibold ${
                              area.merchant_signup_enabled
                                ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-400'
                                : 'bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-400'
                            }`}
                          >
                            {area.merchant_signup_enabled ? 'Active' : 'Inactive'}
                          </button>
                        </td>
                        <td className="px-6 py-3">
                          <button
                            onClick={() => handleToggleCustomer(area, !area.customer_signup_enabled)}
                            disabled={pincodeActionLoading === area.pincode_id + '-customer'}
                            className={`px-2 py-1 rounded text-xs font-semibold ${
                              area.customer_signup_enabled
                                ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-400'
                                : 'bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-400'
                            }`}
                          >
                            {area.customer_signup_enabled ? 'Active' : 'Inactive'}
                          </button>
                        </td>
                        <td className="px-6 py-3 text-right">
                          <button
                            onClick={() => setRemoveTarget(area)}
                            className="text-xs text-red-500 hover:text-red-700 dark:hover:text-red-400 font-medium"
                          >
                            <XCircle size={15} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Delete Area Safety Modal */}
      <ConfirmModal
        open={!!deleteAreaTarget}
        title={`Serviceable Area Delete Karein?`}
        description={
          deleteAreaTarget
            ? `Area "${deleteAreaTarget.area_name}" ko delete karne se iske saare linked pincodes aur service gates remove ho jayenge. Agar is area mein active orders ya merchants hain to database delete-safety trigger isko block karega.`
            : ''
        }
        confirmLabel="Haan, Delete Karein"
        cancelLabel="Cancel"
        destructive
        loading={deletingArea}
        onConfirm={confirmDeleteArea}
        onCancel={() => setDeleteAreaTarget(null)}
      />

      {/* Delete Pincode Modal (Legacy) */}
      <ConfirmModal
        open={!!removeTarget}
        title="List se Remove karein?"
        description={removeTarget ? `${removeTarget.pincode} (${removeTarget.district_name}, ${removeTarget.state_name}) ko list se remove karna hai?` : ''}
        confirmLabel="Haan, Remove karein"
        cancelLabel="Cancel"
        destructive
        loading={removingPincode}
        onConfirm={confirmRemovePincode}
        onCancel={() => setRemoveTarget(null)}
      />
    </div>
  );
}

export default ServiceableAreaControlView;
